/**
 * UIStateContext - Gestión centralizada del estado UI
 *
 * Consolida estados de UI que estaban dispersos en AppShell:
 * - Fullscreen states (Drive, Chat, Viewer, GraphPro, Studios)
 * - Collapsed states (Drive)
 * - Panel sizes (Drive width)
 * - Modal states (Profile, ChangePassword)
 * - Mobile states (Menu, Studio)
 *
 * Refactorizado desde AppShell.tsx siguiendo Clean Code
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

// Types
interface UIState {
  // Fullscreen states
  isDriveCollapsed: boolean;
  isChatFullscreen: boolean;
  isViewerFullscreen: boolean;
  isGraphProFullscreen: boolean;
  isStudiosFullscreen: boolean;
  isDriveFullscreen: boolean;

  // Fullscreen containers
  chatFullscreenContainer: HTMLElement | null;
  viewerFullscreenContainer: HTMLElement | null;
  graphProFullscreenContainer: HTMLElement | null;
  studiosFullscreenContainer: HTMLElement | null;
  driveFullscreenContainer: HTMLElement | null;

  // Panel sizes
  driveWidth: number;

  // Modal states
  showProfileModal: boolean;
  showChangePasswordModal: boolean;

  // Mobile states
  isMobileMenuOpen: boolean;
  isMobileStudioOpen: boolean;

  // Input state
  inputMessage: string;
  savedSnippetId: string | null;

  // Conversation creation
  isCreatingConversation: boolean;
}

interface UIStateActions {
  // Drive actions
  setIsDriveCollapsed: (collapsed: boolean) => void;
  setDriveWidth: (width: number) => void;
  toggleDrive: () => void;

  // Fullscreen actions
  setChatFullscreen: (fullscreen: boolean, container?: HTMLElement | null) => void;
  setViewerFullscreen: (fullscreen: boolean, container?: HTMLElement | null) => void;
  setGraphProFullscreen: (fullscreen: boolean, container?: HTMLElement | null) => void;
  setStudiosFullscreen: (fullscreen: boolean, container?: HTMLElement | null) => void;
  setDriveFullscreen: (fullscreen: boolean, container?: HTMLElement | null) => void;

  // Modal actions
  setShowProfileModal: (show: boolean) => void;
  setShowChangePasswordModal: (show: boolean) => void;

  // Mobile actions
  setIsMobileMenuOpen: (open: boolean) => void;
  setIsMobileStudioOpen: (open: boolean) => void;

  // Input actions
  setInputMessage: (message: string) => void;
  setSavedSnippetId: (id: string | null) => void;

  // Conversation actions
  setIsCreatingConversation: (creating: boolean) => void;

  // Utility
  closeAllFullscreens: () => void;
  closeAllModals: () => void;
}

type UIStateContextType = UIState & UIStateActions;

const UIStateContext = createContext<UIStateContextType | undefined>(undefined);

// Default values
const DEFAULT_DRIVE_WIDTH = 280;

// Helper: Load from localStorage
const loadFromLocalStorage = <T,>(key: string, defaultValue: T): T => {
  try {
    const saved = localStorage.getItem(key);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Helper: Save to localStorage
const saveToLocalStorage = <T,>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export function UIStateProvider({ children }: { children: ReactNode }) {
  // Drive states
  const [isDriveCollapsed, setIsDriveCollapsedState] = useState(() =>
    loadFromLocalStorage('isDriveCollapsed', false)
  );
  const [driveWidth, setDriveWidthState] = useState(() =>
    loadFromLocalStorage('driveWidth', DEFAULT_DRIVE_WIDTH)
  );
  const [isDriveFullscreen, setIsDriveFullscreenState] = useState(false);
  const [driveFullscreenContainer, setDriveFullscreenContainer] = useState<HTMLElement | null>(
    null
  );

  // Other fullscreen states
  const [isChatFullscreen, setIsChatFullscreenState] = useState(false);
  const [chatFullscreenContainer, setChatFullscreenContainer] = useState<HTMLElement | null>(null);
  const [isViewerFullscreen, setIsViewerFullscreenState] = useState(false);
  const [viewerFullscreenContainer, setViewerFullscreenContainer] = useState<HTMLElement | null>(
    null
  );
  const [isGraphProFullscreen, setIsGraphProFullscreenState] = useState(false);
  const [graphProFullscreenContainer, setGraphProFullscreenContainer] =
    useState<HTMLElement | null>(null);
  const [isStudiosFullscreen, setIsStudiosFullscreenState] = useState(false);
  const [studiosFullscreenContainer, setStudiosFullscreenContainer] = useState<HTMLElement | null>(
    null
  );

  // Modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Mobile states
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileStudioOpen, setIsMobileStudioOpen] = useState(false);

  // Input states
  const [inputMessage, setInputMessage] = useState('');
  const [savedSnippetId, setSavedSnippetId] = useState<string | null>(null);

  // Conversation state
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);

  // Actions with persistence
  const setIsDriveCollapsed = useCallback((collapsed: boolean) => {
    setIsDriveCollapsedState(collapsed);
    saveToLocalStorage('isDriveCollapsed', collapsed);
  }, []);

  const setDriveWidth = useCallback((width: number) => {
    setDriveWidthState(width);
    saveToLocalStorage('driveWidth', width);
  }, []);

  const toggleDrive = useCallback(() => {
    setIsDriveCollapsedState((prev) => !prev);
    saveToLocalStorage('isDriveCollapsed', !isDriveCollapsed);
  }, [isDriveCollapsed]);

  // Fullscreen actions
  const setChatFullscreen = useCallback((fullscreen: boolean, container?: HTMLElement | null) => {
    setIsChatFullscreenState(fullscreen);
    if (container !== undefined) setChatFullscreenContainer(container);
  }, []);

  const setViewerFullscreen = useCallback((fullscreen: boolean, container?: HTMLElement | null) => {
    setIsViewerFullscreenState(fullscreen);
    if (container !== undefined) setViewerFullscreenContainer(container);
  }, []);

  const setGraphProFullscreen = useCallback(
    (fullscreen: boolean, container?: HTMLElement | null) => {
      setIsGraphProFullscreenState(fullscreen);
      if (container !== undefined) setGraphProFullscreenContainer(container);
    },
    []
  );

  const setStudiosFullscreen = useCallback(
    (fullscreen: boolean, container?: HTMLElement | null) => {
      setIsStudiosFullscreenState(fullscreen);
      if (container !== undefined) setStudiosFullscreenContainer(container);
    },
    []
  );

  const setDriveFullscreen = useCallback((fullscreen: boolean, container?: HTMLElement | null) => {
    setIsDriveFullscreenState(fullscreen);
    if (container !== undefined) setDriveFullscreenContainer(container);
  }, []);

  // Utility actions
  const closeAllFullscreens = useCallback(() => {
    setIsChatFullscreenState(false);
    setIsViewerFullscreenState(false);
    setIsGraphProFullscreenState(false);
    setIsStudiosFullscreenState(false);
    setIsDriveFullscreenState(false);
  }, []);

  const closeAllModals = useCallback(() => {
    setShowProfileModal(false);
    setShowChangePasswordModal(false);
  }, []);

  const value: UIStateContextType = useMemo(
    () => ({
      // State
      isDriveCollapsed,
      isChatFullscreen,
      isViewerFullscreen,
      isGraphProFullscreen,
      isStudiosFullscreen,
      isDriveFullscreen,
      chatFullscreenContainer,
      viewerFullscreenContainer,
      graphProFullscreenContainer,
      studiosFullscreenContainer,
      driveFullscreenContainer,
      driveWidth,
      showProfileModal,
      showChangePasswordModal,
      isMobileMenuOpen,
      isMobileStudioOpen,
      inputMessage,
      savedSnippetId,
      isCreatingConversation,

      // Actions
      setIsDriveCollapsed,
      setDriveWidth,
      toggleDrive,
      setChatFullscreen,
      setViewerFullscreen,
      setGraphProFullscreen,
      setStudiosFullscreen,
      setDriveFullscreen,
      setShowProfileModal,
      setShowChangePasswordModal,
      setIsMobileMenuOpen,
      setIsMobileStudioOpen,
      setInputMessage,
      setSavedSnippetId,
      setIsCreatingConversation,
      closeAllFullscreens,
      closeAllModals,
    }),
    [
      isDriveCollapsed,
      isChatFullscreen,
      isViewerFullscreen,
      isGraphProFullscreen,
      isStudiosFullscreen,
      isDriveFullscreen,
      chatFullscreenContainer,
      viewerFullscreenContainer,
      graphProFullscreenContainer,
      studiosFullscreenContainer,
      driveFullscreenContainer,
      driveWidth,
      showProfileModal,
      showChangePasswordModal,
      isMobileMenuOpen,
      isMobileStudioOpen,
      inputMessage,
      savedSnippetId,
      isCreatingConversation,
      setIsDriveCollapsed,
      setDriveWidth,
      toggleDrive,
      setChatFullscreen,
      setViewerFullscreen,
      setGraphProFullscreen,
      setStudiosFullscreen,
      setDriveFullscreen,
      setShowProfileModal,
      setShowChangePasswordModal,
      setIsMobileMenuOpen,
      setIsMobileStudioOpen,
      setInputMessage,
      setSavedSnippetId,
      setIsCreatingConversation,
      closeAllFullscreens,
      closeAllModals,
    ]
  );

  return <UIStateContext.Provider value={value}>{children}</UIStateContext.Provider>;
}

// Hook
export function useUIState() {
  const context = useContext(UIStateContext);
  if (context === undefined) {
    throw new Error('useUIState must be used within a UIStateProvider');
  }
  return context;
}

// Export types
export type { UIState, UIStateActions, UIStateContextType };
